'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

const BRAND = '#3757FF';

export default function StudioHero() {
    const [mounted, setMounted] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => { setMounted(true); }, []);

    useEffect(() => {
        if (!mounted) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        let animId: number;
        let t = 0;
        const resize = () => {
            canvas.width = canvas.offsetWidth * window.devicePixelRatio;
            canvas.height = canvas.offsetHeight * window.devicePixelRatio;
            ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        };
        resize();
        window.addEventListener('resize', resize);
        const draw = () => {
            const w = canvas.offsetWidth;
            const h = canvas.offsetHeight;
            ctx.clearRect(0, 0, w, h);
            t += 0.003;
            const orbs = [
                { x: w * 0.8 + Math.sin(t) * 80, y: h * 0.3 + Math.cos(t * 0.7) * 60, r: 400, color: 'rgba(55,87,255,0.07)' },
                { x: w * 0.1 + Math.cos(t * 0.9) * 60, y: h * 0.6 + Math.sin(t * 1.1) * 50, r: 350, color: 'rgba(55,87,255,0.05)' },
                { x: w * 0.5 + Math.sin(t * 1.3) * 100, y: h * 0.9 + Math.cos(t * 0.5) * 40, r: 280, color: 'rgba(100,130,255,0.06)' },
            ];
            orbs.forEach(orb => {
                const grad = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.r);
                grad.addColorStop(0, orb.color);
                grad.addColorStop(1, 'transparent');
                ctx.fillStyle = grad;
                ctx.beginPath();
                ctx.arc(orb.x, orb.y, orb.r, 0, Math.PI * 2);
                ctx.fill();
            });
            animId = requestAnimationFrame(draw);
        };
        draw();
        return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
    }, [mounted]);

    return (
        <section className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20 px-6 md:px-10" style={{ background: '#FAFAFA' }}>
            <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

            <div className="max-w-7xl mx-auto relative z-10 w-full">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left */}
                    <div>
                        <div className="flex items-center gap-3 mb-8">
                            <Link
                                href="/wisly"
                                className="text-xs font-700 uppercase tracking-widest transition-colors"
                                style={{ color: '#999', letterSpacing: '0.12em' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = BRAND; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#999'; }}
                            >
                                Wisly
                            </Link>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M4 2l4 4-4 4" stroke="#CCC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span className="text-xs font-700 uppercase tracking-widest" style={{ color: BRAND, letterSpacing: '0.12em' }}>Wisly Studio</span>
                        </div>

                        <h1
                            className="font-display font-800 mb-6"
                            style={{ fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)', lineHeight: 1.0, letterSpacing: '-0.03em', color: '#0D0D0D' }}
                        >
                            Everything you need
                            <br />
                            <span style={{ color: BRAND }}>to teach and manage.</span>
                        </h1>

                        <p className="text-xl leading-relaxed mb-4" style={{ color: '#444', maxWidth: '520px' }}>
                            Wisly Studio is the operational workspace for tutors and educational institutions — built to handle every dimension of running an educational operation.
                        </p>

                        <div
                            className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl mb-10"
                            style={{ background: 'rgba(55,87,255,0.06)', border: '1px solid rgba(55,87,255,0.12)' }}
                        >
                            {['Teach', 'Manage', 'Organize', 'Grow'].map((word, i) => (
                                <React.Fragment key={word}>
                                    <span className="text-sm font-700" style={{ color: BRAND }}>{word}</span>
                                    {i < 3 && <span className="w-1 h-1 rounded-full" style={{ background: 'rgba(55,87,255,0.35)' }} />}
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="flex flex-wrap gap-3">
                            <a
                                href="#schedules"
                                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-sm font-700 text-white transition-all duration-300"
                                style={{ background: BRAND, boxShadow: '0 8px 28px rgba(55,87,255,0.28)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 36px rgba(55,87,255,0.35)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 28px rgba(55,87,255,0.28)'; }}
                            >
                                Explore Studio
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M2.5 7h9M7.5 3.5l3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </a>
                            <a
                                href="#institutions"
                                className="inline-flex items-center gap-2 px-7 py-4 rounded-xl text-sm font-600 transition-all duration-300"
                                style={{ color: '#333', border: '1.5px solid rgba(13,13,13,0.15)' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = BRAND; (e.currentTarget as HTMLElement).style.color = BRAND; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(13,13,13,0.15)'; (e.currentTarget as HTMLElement).style.color = '#333'; }}
                            >
                                For instructors
                            </a>
                        </div>
                    </div>

                    {/* Right: Studio workspace mockup */}
                    <div className="relative">
                        <div
                            className="rounded-3xl p-6 relative overflow-hidden"
                            style={{ background: 'white', boxShadow: '0 24px 80px rgba(55,87,255,0.12)', border: '1px solid rgba(55,87,255,0.1)' }}
                        >
                            {/* Header bar */}
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <p className="text-xs font-700 uppercase tracking-widest mb-1" style={{ color: '#999', letterSpacing: '0.12em' }}>Studio Dashboard</p>
                                    <p className="text-lg font-800" style={{ color: '#0D0D0D' }}>Good morning, Mr. Osei 👋</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full" style={{ background: '#10B981' }} />
                                    <span className="text-xs font-600" style={{ color: '#10B981' }}>Live session active</span>
                                </div>
                            </div>

                            {/* Stats row */}
                            <div className="grid grid-cols-4 gap-3 mb-5">
                                {[
                                    { label: 'Students', value: '148' },
                                    { label: 'Classes Today', value: '4' },
                                    { label: 'Pending Reviews', value: '12' },
                                    { label: 'Avg Score', value: '81%' },
                                ].map(stat => (
                                    <div key={stat.label} className="rounded-xl p-3 text-center" style={{ background: '#F7F8FF', border: '1px solid rgba(55,87,255,0.08)' }}>
                                        <p className="text-lg font-800 mb-0.5" style={{ color: BRAND }}>{stat.value}</p>
                                        <p className="text-xs font-600" style={{ color: '#888' }}>{stat.label}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Today's schedule */}
                            <div className="rounded-2xl p-4 mb-4" style={{ background: '#F7F8FF', border: '1px solid rgba(55,87,255,0.1)' }}>
                                <p className="text-xs font-700 uppercase tracking-widest mb-3" style={{ color: '#999', letterSpacing: '0.12em' }}>Today's Schedule</p>
                                <div className="flex flex-col gap-2.5">
                                    {[
                                        { time: '09:00', subject: 'Algebra — Grade 10', status: 'done', students: 24 },
                                        { time: '11:00', subject: 'Calculus — Grade 12', status: 'live', students: 18 },
                                        { time: '14:00', subject: 'Statistics — Grade 11', status: 'upcoming', students: 22 },
                                    ].map(cls => (
                                        <div key={cls.time} className="flex items-center gap-3">
                                            <span className="text-xs font-700 w-12 flex-shrink-0" style={{ color: '#888' }}>{cls.time}</span>
                                            <div className="flex-1">
                                                <p className="text-xs font-700" style={{ color: '#0D0D0D' }}>{cls.subject}</p>
                                                <p className="text-xs font-500" style={{ color: '#AAA' }}>{cls.students} students</p>
                                            </div>
                                            <span
                                                className="text-xs font-700 px-2.5 py-1 rounded-full"
                                                style={{
                                                    background: cls.status === 'live' ? BRAND : cls.status === 'done' ? 'rgba(16,185,129,0.1)' : 'rgba(55,87,255,0.08)',
                                                    color: cls.status === 'live' ? 'white' : cls.status === 'done' ? '#10B981' : '#888',
                                                }}
                                            >
                                                {cls.status === 'live' ? '● Live' : cls.status === 'done' ? 'Done' : 'Upcoming'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Quick actions */}
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    { label: 'New Assessment', icon: '📋' },
                                    { label: 'Add Student', icon: '👤' },
                                    { label: 'View Reports', icon: '📊' },
                                ].map(action => (
                                    <div
                                        key={action.label}
                                        className="rounded-xl p-3 text-center cursor-pointer transition-all duration-200"
                                        style={{ background: '#F7F8FF', border: '1px solid rgba(55,87,255,0.08)' }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = BRAND; (e.currentTarget as HTMLElement).style.background = 'rgba(55,87,255,0.05)'; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(55,87,255,0.08)'; (e.currentTarget as HTMLElement).style.background = '#F7F8FF'; }}
                                    >
                                        <p className="text-base mb-1">{action.icon}</p>
                                        <p className="text-xs font-600" style={{ color: '#555' }}>{action.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Floating badge */}
                        <div
                            className="absolute -bottom-4 -right-4 rounded-2xl px-5 py-3 flex items-center gap-3"
                            style={{ background: 'white', boxShadow: '0 8px 32px rgba(55,87,255,0.15)', border: '1px solid rgba(55,87,255,0.1)' }}
                        >
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(55,87,255,0.1)' }}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M2 10l3-4 2.5 2.5 3-4.5 2.5 3" stroke={BRAND} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-xs font-800" style={{ color: '#0D0D0D' }}>Student performance</p>
                                <p className="text-xs font-600" style={{ color: '#888' }}>+11% this month</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
